import React, { useState } from 'react'
import { useLoad } from '../hooks/load'
import { Drawer } from '../drawer/drawer'
import { Pill } from '../sso-workspace/pill'
import { formatDateTime, serverMessage } from '../sso-workspace/format'
import { contentGet } from './content-api'
import { scopeOf } from './scope'
import { providerLabel, statusLabel, statusMeaning, statusTone, templateLabel } from './labels'
import { eventSentence, placeText } from './event-wording'
import './email-activity.scss'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'
import { NotifyTimelineLink } from '../../layout/authorized/admin/notify/email-activity-link'

const GONE = "This email isn't available. It may be older than a year, or not one your organization was sent."

// One email in a side panel over the Email activity list (owner 2026-10-07:
// "like the audit History drawer"): the shared Drawer, sliding in from the
// right on a desktop and filling a phone's screen. The page names the open
// email in its URL; onClose goes back to the list as it was.
export const EmailPanel = ({ id, system = false, onClose }) => {
  const [title, setTitle] = useState('Email')
  return (
    <Drawer open title={title} label={title} onClose={onClose} size="md" phone="full">
      <MessageDetail key={id} id={id} system={system} onLoaded={d => setTitle(templateLabel(d.templateKey))} />
    </Drawer>
  )
}

// What the panel shows: what was sent, to whom, its totals, its delivery
// timeline in words (event-wording.js), and its details; a message the dev
// catcher kept also shows its rendered preview (in a sandboxed frame: no
// scripts, no same-origin access) and its links.
//
// `system` is the staff Internal tools view: it also names the
// organization, the email's ids and codes, each open's and click's IP, and
// the provider's raw event behind "Show raw data". The organization's page
// never gets those from the server (content ForOrganization), and never
// shows them either.
export const MessageDetail = ({ id, system = false, onLoaded }) => {
  const [detail, setDetail] = useState(undefined)
  const [raw, setRaw] = useState(false)
  const [error, failure, setError] = useErrorLine()

  useLoad(async () => {
    try {
      const body = await contentGet(`email-activity/message/${id}`, { scope: scopeOf(system) })
      if (!body || typeof body !== 'object') throw new Error('empty response')
      setDetail(body)
      if (onLoaded) onLoaded(body)
    } catch (err) {
      setDetail(null)
      setError(err && err.status === 404 ? GONE : serverMessage(err, "This email couldn't be loaded."), err)
    }
  }, [id, system])

  if (detail === undefined) return <p className="sw-hint m-0" role="status">Loading…</p>
  if (detail === null) return <div><p className="text-danger small m-0" role="alert">{error}</p><ErrorReference error={failure} className="mt-1" /></div>

  const events = detail.events || []
  const totals = detail.totals || {}
  const preview = system ? detail.preview : null
  const variables = system ? Object.entries(detail.variables || {}).sort(([a], [b]) => a.localeCompare(b)) : []
  const located = events.some(e => e.location)
  const hasRaw = system && events.some(e => e.payload)

  return (
    <div className="ea-panel">
      <p className="sw-hint m-0 ea-panel-to">{`To ${detail.recipient}`}</p>

      <div className="ea-panel-status">
        <Pill tone={statusTone(detail.status)} title={formatDateTime(detail.statusAt)}>{statusLabel(detail.status)}</Pill>
        {detail.caught && <Pill tone="info" title="Kept by the dev catcher, not sent">Caught</Pill>}
        {statusMeaning(detail.status) && <p className="m-0 small">{statusMeaning(detail.status)}</p>}
      </div>

      <Totals totals={totals} />

      <section className="ea-panel-section" aria-labelledby={`ea-timeline-${id}`}>
        <div className="ea-panel-head">
          <h3 className="h6 fw-semibold m-0" id={`ea-timeline-${id}`}>What happened</h3>
          {hasRaw &&
            <button type="button" className="btn btn-sm sw-btn" aria-pressed={raw ? 'true' : 'false'} onClick={() => setRaw(r => !r)}>
              {raw ? 'Hide raw data' : 'Show raw data'}
            </button>}
        </div>
        <ol className="ea-timeline" aria-label="Delivery timeline">
          <FirstItem detail={detail} />
          {events.map(e => <EventItem key={e.id} event={e} system={system} raw={raw} />)}
        </ol>
        {events.length === 0 && !detail.caught &&
          <p className="sw-hint mt-2 mb-0">No delivery events yet. The email service reports them within a few minutes of sending.</p>}
        {system && detail.geoAvailable === false &&
          <p className="sw-hint mt-2 mb-0">Locations aren't available: this server has no IP location database installed.</p>}
      </section>

      <section className="ea-panel-section" aria-labelledby={`ea-details-${id}`}>
        <h3 className="h6 fw-semibold m-0" id={`ea-details-${id}`}>Details</h3>
        <dl className="sw-dl">
          <dt>Email</dt>
          <dd>{templateLabel(detail.templateKey)}{system && <span className="sw-mono sw-hint"> {detail.templateKey}</span>}</dd>
          <dt>To</dt><dd>{detail.recipient}</dd>
          {system && detail.organizationName && <><dt>Organization</dt><dd>{detail.organizationName}</dd></>}
          <dt>Sent</dt><dd>{formatDateTime(detail.createdAt)}</dd>
          <dt>Sent through</dt><dd>{providerLabel(detail.provider)}</dd>
          {system && detail.subjectRef && <><dt>About</dt><dd className="sw-mono">{detail.subjectRef}</dd></>}
          {system && detail.providerMessageID && <><dt>Provider message</dt><dd className="sw-mono">{detail.providerMessageID}</dd></>}
          {system && detail.error && <><dt>Error</dt><dd className="sw-mono text-danger">{detail.error}</dd></>}
          <NotifyTimelineLink row messageId={detail.id || id} notificationId={detail.notificationId} system={system} />
        </dl>
      </section>

      {variables.length > 0 &&
        <section className="ea-panel-section" aria-labelledby={`ea-values-${id}`}>
          <h3 className="h6 fw-semibold m-0" id={`ea-values-${id}`}>Values it was sent with</h3>
          <p className="sw-hint small m-0">Links keep only their site, and anything secret is never stored.</p>
          <dl className="sw-dl">
            {variables.map(([k, v]) => <React.Fragment key={k}><dt className="sw-mono">{k}</dt><dd>{v || <span className="sw-hint">empty</span>}</dd></React.Fragment>)}
          </dl>
        </section>}

      {preview &&
        <section className="ea-panel-section" aria-labelledby={`ea-preview-${id}`}>
          <h3 className="h6 fw-semibold m-0" id={`ea-preview-${id}`}>Preview <Pill tone="info">Not sent</Pill></h3>
          <p className="sw-hint small m-0">Caught on this dev environment instead of being sent. {preview.subject}</p>
          <iframe className="ea-preview" title="Email preview" sandbox="" srcDoc={preview.html} />
          {(preview.links || []).length > 0 &&
            <div>
              <h4 className="h6 fw-semibold">Links</h4>
              <ul className="ea-links">
                {preview.links.map(l =>
                  <li key={`${l.name}-${l.url}`}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer"><span className="sw-mono">{l.name}</span>: {l.url}</a>
                  </li>)}
              </ul>
            </div>}
        </section>}

      {located && <p className="small sw-hint m-0 ea-credit">IP location by <a href="https://db-ip.com" target="_blank" rel="noreferrer">DB-IP</a></p>}
    </div>
  )
}

// The counts over the email's events.
const Totals = ({ totals: t }) => {
  const cell = (label, n, sub, wide) =>
    <div className={`ea-total${wide ? ' ea-total-wide' : ''}`} key={label}>
      <dt>{label}</dt>
      <dd>{n || 0}</dd>
      {sub && <dd className="ea-total-sub">{sub}</dd>}
    </div>
  const opensSub = t.opens ? `${t.personOpens || 0} likely a person, ${t.machineOpens || 0} automated` : ''
  return (
    <>
      <dl className="ea-totals" role="group" aria-label="Totals">
        {cell('Delivered', t.delivered)}
        {cell('Bounced', t.bounced)}
        {cell('Deferred', t.deferred)}
        {cell('Opened', t.opens, opensSub, true)}
        {cell('Clicked', t.clicks)}
        {t.dropped > 0 && cell('Not sent', t.dropped)}
        {t.spamReports > 0 && cell('Marked as spam', t.spamReports)}
        {t.unsubscribes > 0 && cell('Unsubscribed', t.unsubscribes)}
      </dl>
      {t.firstEventAt &&
        <p className="sw-hint m-0 ea-span">
          <span>{`First event ${formatDateTime(t.firstEventAt)}`}</span>
          {t.lastEventAt && t.lastEventAt !== t.firstEventAt && <span>{`Last event ${formatDateTime(t.lastEventAt)}`}</span>}
        </p>}
    </>
  )
}

// The timeline starts when EducationXR handed the email to the email service.
const FirstItem = ({ detail }) => {
  const failed = detail.status === 'failed'
  const title = detail.caught || detail.provider === 'catcher'
    ? 'Caught by the dev catcher, not sent'
    : failed ? "Couldn't be sent" : 'Sent'
  return (
    <li data-tone={failed ? 'bad' : undefined}>
      <span className="ea-event-title">{title}</span>
      <span className="sw-hint ea-event-when">{formatDateTime(detail.createdAt)}</span>
      {!failed && !detail.caught && <span className="ea-event-detail">EducationXR handed it to the email service.</span>}
    </li>
  )
}

const EventItem = ({ event: e, system, raw }) => {
  const s = eventSentence(e)
  const place = placeText(e.location)
  const saidBy = e.type === 'dropped' ? 'The email service said' : 'The receiving server said'
  return (
    <li data-tone={s.tone || undefined}>
      <span className="ea-event-title">{s.title}</span>
      <span className="sw-hint ea-event-when">{formatDateTime(e.occurredAt)}</span>
      {s.detail && <span className="ea-event-detail">{s.detail}</span>}
      {s.said && <span className="ea-event-said">{`${saidBy}: “${s.said}”`}</span>}
      {place && <span className="ea-event-detail">{`Near ${place}`}</span>}
      {system && e.ip && <span className="ea-event-ip sw-mono">{`IP ${e.ip}${e.network ? ` · ${e.network}` : ''}`}</span>}
      {system && raw && e.payload && <pre className="ea-raw" aria-label="Raw event">{JSON.stringify(e.payload, null, 2)}</pre>}
    </li>
  )
}
